import React, { useEffect, useMemo, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import concat from 'lodash/concat';
import isEmpty from 'lodash/isEmpty';

import Button from '../../../Button';
import Icon from '../../../Icon';
import { COLORS } from '../../../../constants/styles';
import { actions as inviteGuest } from '../../../../redux/healthboard/requests/invite-guest-to-virtual-room-req';
import { selectCurrentAppointment } from '../../../../redux/appointmnents/selectors';
import {
  selectAppointmentIdFromConfig,
  selectCallInvitationsExisting,
  selectCallInvitationsSendError,
  selectCallInvitationsSendLoading,
} from '../../../../redux/healthboard/selectors';
import { isEmail } from '../../../../utils/validations/isEmail';
import InviteGuestInput from './InviteGuestInput';
import ErrorBlock from '../../../common/ErrorBlock';
import { ERROR_CODES } from '../../../../constants/errors';
import styles from './styles.styl';

const initialInputState = {
  email: '',
  id: Math.random(),
};

function InviteGuestForm({ close, intl: { formatMessage } }) {
  const dispatch = useDispatch();
  const [isError, setIsError] = useState(false);
  const [inputList, setInputList] = useState([initialInputState]);
  const errorRef = useRef(null);

  const appointment = useSelector(selectCurrentAppointment);
  const safeAppointmentId = useSelector(selectAppointmentIdFromConfig);
  const existing = useSelector(selectCallInvitationsExisting);
  const error = useSelector(selectCallInvitationsSendError);
  const loading = useSelector(selectCallInvitationsSendLoading);

  const appointmentId = get(appointment, 'id', safeAppointmentId);
  errorRef.current = error;

  const inviteeData = uniq(inputList.map(el => el.email));
  const hasEmptyValues = inputList.some(el => isEmpty(el.email));

  const handleChange = (ev, id) => {
    const currentInputList = inputList.map(el => {
      return el.id === id ? { ...el, email: ev } : el;
    });
    setInputList(currentInputList);
    if (errorRef.current) {
      dispatch(inviteGuest.clear());
    }
  };

  const handleAdd = () => {
    const values = [...inputList];
    values.push({ ...initialInputState, id: Math.random() });
    setInputList(values);
  };

  useEffect(() => () => dispatch(inviteGuest.clear()), []);

  const errorMessage = useMemo(() => {
    if (!error) return '';
    const code = get(error, 'error.code');
    const id =
      code === ERROR_CODES.INVALID_PHONE_NUMBER
        ? 'errors.validation.invalidLoginPhone'
        : 'errors.common';
    return formatMessage({ id });
  }, [error]);

  const handleSubmit = () => {
    const guests = [];
    Object.values(inviteeData).forEach(value => {
      if (
        isEmail(value) &&
        !concat(
          existing,
          guests.filter(el => el.email).map(el => el.email),
        ).includes(value)
      ) {
        guests.push({ email: value });
      }
      if (
        !isEmail(value) &&
        !concat(
          existing,
          guests.filter(el => el.phoneNumber).map(el => el.phoneNumber),
        ).includes(value)
      ) {
        guests.push({ phone_number: value });
      }
    });
    if (guests.length > 0) {
      const data = { appointmentId, guests, close };
      dispatch(inviteGuest.init(data));
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.description}>
        {formatMessage({ id: 'virtualVisit.inviteGuest.description' })}
      </div>
      {inputList.map(item => (
        <InviteGuestInput
          key={item.id}
          item={item}
          formatMessage={formatMessage}
          onChange={handleChange}
          onError={setIsError}
        />
      ))}
      {errorMessage && <ErrorBlock errors={errorMessage} />}

      <div className={styles.btnAdd}>
        <button type="button" onClick={() => handleAdd()}>
          <Icon name="add-text" size={16} style={{ fill: COLORS.stormyBlue }} />
          <span>
            {formatMessage({ id: 'virtualVisit.inviteGuest.addGuest' })}
          </span>
        </button>
      </div>

      <div className={styles.btnsSubmit}>
        <Button
          type="button"
          color="primary"
          raised
          disabled={isError || hasEmptyValues || loading}
          onClick={handleSubmit}
        >
          {formatMessage({ id: 'community.menu.invite' })}
        </Button>
        <Button type="button" raised color="accent" onClick={close}>
          {formatMessage({ id: 'common.cancel' })}
        </Button>
      </div>
    </div>
  );
}

InviteGuestForm.propTypes = {
  intl: PropTypes.object,
  close: PropTypes.func,
};

export default injectIntl(InviteGuestForm);



// WEBPACK FOOTER //
// ./src/components/VirtualVideoRoom/Components/InviteGuestForm/index.jsx